import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { servicesAPI, invoicesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Plus, Trash2, Loader2 } from 'lucide-react';

const EditInvoiceDialog = ({ 
  open, 
  onOpenChange, 
  invoice,
  onSuccess = () => {} 
}) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [hstOverride, setHstOverride] = useState(null);
  
  const { toast } = useToast();

  useEffect(() => {
    if (open && invoice) {
      fetchServices();
      // Initialize edit form with invoice data
      setEditInvoice({
        ...invoice,
        invoiceDate: invoice.invoiceDate ? invoice.invoiceDate.split('T')[0] : '',
        dueDate: invoice.dueDate ? invoice.dueDate.split('T')[0] : '',
        lineItems: invoice.lineItems || [{ service: '', year: '', description: '', amount: 0 }],
        hstRate: (invoice.hstRate || 0.13) * 100, // Convert to percentage for display
        taxMode: invoice.taxMode || 'exclusive'
      });
      setHstOverride(null);
    }
  }, [open, invoice]);

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const addLineItem = () => {
    setEditInvoice(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { service: '', year: new Date().getFullYear().toString(), description: '', amount: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    setEditInvoice(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setEditInvoice(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: field === 'amount' ? parseFloat(value) || 0 : value };
          // Auto-fill amount when service is selected
          if (field === 'service') {
            const selectedService = services.find(s => s.name === value);
            if (selectedService?.defaultAmount) {
              updatedItem.amount = selectedService.defaultAmount;
            }
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const calculateTotals = () => {
    if (!editInvoice) return { subtotal: 0, hstAmount: 0, grandTotal: 0 };
    const lineTotal = editInvoice.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    if (editInvoice.taxMode === 'inclusive') {
      const rate = editInvoice.hstRate / 100;
      const subtotal = lineTotal / (1 + rate);
      const hstAmount = hstOverride !== null ? hstOverride : lineTotal - subtotal;
      const grandTotal = lineTotal;
      return { subtotal: grandTotal - hstAmount, hstAmount, grandTotal };
    } else {
      const subtotal = lineTotal;
      const hstAmount = hstOverride !== null ? hstOverride : subtotal * (editInvoice.hstRate / 100);
      const grandTotal = subtotal + hstAmount;
      return { subtotal, hstAmount, grandTotal };
    }
  };

  const handleUpdateInvoice = async () => {
    if (!editInvoice.dueDate) {
      toast({ title: 'Error', description: 'Due date is required', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const { subtotal, hstAmount, grandTotal } = calculateTotals();
      const invoiceData = {
        ...editInvoice,
        invoiceDate: editInvoice.invoiceDate ? new Date(editInvoice.invoiceDate).toISOString() : new Date().toISOString(),
        dueDate: new Date(editInvoice.dueDate).toISOString(),
        hstRate: editInvoice.hstRate / 100, // Convert back to decimal
        subtotal,
        hstAmount,
        grandTotal,
        taxMode: editInvoice.taxMode
      };

      await invoicesAPI.update(invoice._id, invoiceData);
      toast({ title: 'Success', description: 'Invoice updated successfully' });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast({ title: 'Error', description: 'Failed to update invoice', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  if (!editInvoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Invoice - {editInvoice.invoiceNumber}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {/* Invoice Number and Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Invoice #</Label>
              <Input
                value={editInvoice.invoiceNumber || ''}
                onChange={(e) => setEditInvoice(prev => ({ ...prev, invoiceNumber: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Invoice Date</Label>
              <Input
                type="date"
                value={editInvoice.invoiceDate}
                onChange={(e) => setEditInvoice(prev => ({ ...prev, invoiceDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={editInvoice.dueDate}
                onChange={(e) => setEditInvoice(prev => ({ ...prev, dueDate: e.target.value }))}
              />
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Line Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="gap-1">
                <Plus className="h-3 w-3" /> Add Line
              </Button>
            </div>

            <div className="space-y-3">
              {editInvoice.lineItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    {index === 0 && <Label className="text-xs text-gray-500">Service</Label>}
                    <Select
                      value={item.service || ''}
                      onValueChange={(v) => updateLineItem(index, 'service', v)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service._id} value={service.name}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    {index === 0 && <Label className="text-xs text-gray-500">Year</Label>}
                    <Input
                      placeholder="Year"
                      value={item.year || ''}
                      onChange={(e) => updateLineItem(index, 'year', e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-4">
                    {index === 0 && <Label className="text-xs text-gray-500">Description</Label>}
                    <Input
                      placeholder="Description"
                      value={item.description || ''}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-2">
                    {index === 0 && <Label className="text-xs text-gray-500">Amount</Label>}
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={item.amount || ''}
                      onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-1">
                    {editInvoice.lineItems.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        className="h-9 w-9 p-0 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t pt-4">
            <div className="flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm items-center mb-3">
                  <span className="font-medium">Tax Calculation:</span>
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                    <button
                      type="button"
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${editInvoice.taxMode === 'exclusive' ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                      onClick={() => { setEditInvoice(prev => ({ ...prev, taxMode: 'exclusive' })); setHstOverride(null); }}
                      data-testid="edit-tax-mode-exclusive"
                    >
                      Exclusive
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${editInvoice.taxMode === 'inclusive' ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                      onClick={() => { setEditInvoice(prev => ({ ...prev, taxMode: 'inclusive' })); setHstOverride(null); }}
                      data-testid="edit-tax-mode-inclusive"
                    >
                      Inclusive
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-2">
                  {editInvoice.taxMode === 'inclusive' 
                    ? 'Amounts include HST — tax is back-calculated from totals' 
                    : 'HST is added on top of the line item amounts'}
                </p>
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>${totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span>GST/HST Rate:</span>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      className="w-16 h-7 text-right"
                      value={editInvoice.hstRate}
                      onChange={(e) => { setEditInvoice(prev => ({ ...prev, hstRate: parseFloat(e.target.value) || 0 })); setHstOverride(null); }}
                    />
                    <span>%</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm items-center">
                  <span>GST/HST ({editInvoice.hstRate}%):</span>
                  <div className="flex items-center gap-1">
                    <span>$</span>
                    <Input
                      type="number"
                      className="w-24 h-7 text-right"
                      value={hstOverride !== null ? hstOverride : totals.hstAmount.toFixed(2)}
                      onChange={(e) => setHstOverride(parseFloat(e.target.value) || 0)}
                      data-testid="edit-hst-amount-input"
                    />
                  </div>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Grand Total:</span>
                  <span>${totals.grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpdateInvoice} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditInvoiceDialog;
